import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import { BRANDS } from "@/data/site";
import Reveal from "@/components/Reveal";

export default function BrandShowcase() {
  const { slug } = useParams();
  const brand = BRANDS.find((b) => b.slug === slug);
  if (!brand) return <Navigate to="/brands" replace />;
  const others = BRANDS.filter((b) => b.slug !== slug);

  return (
    <div data-testid={`page-brand-${brand.slug}`} className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-12 bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Link to="/brands" data-testid="brand-back" className="kbd link-underline text-[var(--ink-3)]">← All Brands</Link>
            <div className="bg-white border border-[var(--line-soft)] rounded-xl p-3 inline-flex mt-5">
              <img src={brand.logo} alt={`${brand.name} logo`} className="h-16 md:h-20 w-auto object-contain" />
            </div>
            <div className="overline overline-green mt-6">{brand.category}</div>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-3 leading-tight text-[var(--ink)] font-semibold">
              {brand.tagline}
            </h1>
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-lg">{brand.story}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={brand.site} data-testid="brand-visit-official" className="btn-primary">
                Visit Official Website <ArrowUpRight size={14} />
              </a>
              <Link to="/products" data-testid="brand-shop" className="btn-outline">Shop Pantry <ArrowRight size={14} /></Link>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="aspect-[5/4] overflow-hidden rounded-2xl">
              <img src={brand.hero} alt={brand.name} className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">What makes it different</div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                Built around four <span className="text-[var(--green)]">non-negotiables.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {brand.pillars.map((p, i) => (
              <Reveal key={p.label} delay={i * 0.05}>
                <div className="card-soft p-6 h-full">
                  <div className="w-12 h-12 rounded-full bg-[var(--green)] text-white flex items-center justify-center mb-5 font-serif-display text-lg">
                    0{i + 1}
                  </div>
                  <h3 className="font-serif-display text-xl font-semibold text-[var(--ink)] leading-tight">{p.label}</h3>
                  <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12">
            {brand.nutrition.map(([label, value]) => (
              <div key={label} className="card-soft p-6 text-center">
                <div className="font-serif-display text-3xl md:text-4xl text-[var(--green)] font-semibold">{value}</div>
                <div className="kbd text-[var(--ink-3)] mt-3">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline overline-green">Inside the experience</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold max-w-2xl">
              Imagery from <span className="text-[var(--green)]">the floor.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            {brand.gallery.map((src, i) => (
              <div key={i} className="aspect-[4/5] overflow-hidden rounded-2xl">
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="rounded-2xl bg-[var(--green-dark)] text-white px-8 md:px-14 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="overline text-[var(--gold-soft)]">Continue on {brand.name.split("—")[0].trim()}</div>
              <h3 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight font-semibold">
                See the full menu, locations and reservations <span className="text-[var(--gold)]">on the official site.</span>
              </h3>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a href={brand.site} data-testid="brand-bottom-visit-official" className="btn-gold">
                Visit Official Website <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <h3 className="font-serif-display text-2xl md:text-4xl font-semibold text-[var(--ink)] max-w-2xl">
              More from the ecosystem.
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
            {others.map((b) => (
              <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`related-${b.slug}`} className="card-soft block overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={b.hero} alt={b.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <div className="kbd text-[var(--green)]">{b.category}</div>
                    <div className="font-serif-display text-lg font-semibold mt-1">{b.name.split("—")[0].trim()}</div>
                  </div>
                  <ArrowRight size={18} className="text-[var(--green)]" />
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/brands" data-testid="back-to-brands" className="btn-outline">View all brands <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
